import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FUND_META } from "@/lib/budget";

const toCents = (v) => {
  const s = String(v ?? "").trim();
  if (s === "") return null;
  const n = Number(s.replace(/[$,]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};
const toDollars = (c) => (c == null ? "" : (c / 100).toString());
const NONE = "__none__";

export function TransactionDialog({ open, onOpenChange, artistId, albums = [], tx, onSaved }) {
  const editing = !!(tx && tx.id);
  const [fund, setFund] = useState("recording");
  const [albumId, setAlbumId] = useState(NONE);
  const [isSingle, setIsSingle] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [vendor, setVendor] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [apGroup, setApGroup] = useState(NONE);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [include, setInclude] = useState(true);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFund(tx?.fund || "recording");
    setAlbumId(tx?.album_id || NONE);
    setIsSingle(tx?.is_single || false);
    setBookmarked(tx?.bookmarked || false);
    setVendor(tx?.vendor || "");
    setDescription(tx?.description || "");
    setAmount(toDollars(tx?.amount_cents));
    setApGroup(tx?.ap_group || NONE);
    setPaymentStatus(tx?.payment_status || "");
    setInclude(tx?.include_in_rollup ?? true);
    setDate(tx?.date || "");
    setNote(tx?.note || "");
  }, [open, tx]);

  const submit = async () => {
    setSaving(true);
    const payload = {
      fund,
      album_id: albumId === NONE ? null : albumId,
      is_single: isSingle,
      bookmarked,
      vendor: vendor.trim() || null,
      description: description.trim() || null,
      amount_cents: toCents(amount),
      currency: "USD",
      ap_group: bookmarked ? "Bookmarked" : (apGroup === NONE ? null : apGroup),
      payment_status: bookmarked ? (paymentStatus.trim() || "Bookmarked / planned - not paid") : (paymentStatus.trim() || null),
      include_in_rollup: include,
      date: date.trim() || null,
      note: note.trim() || null,
    };
    try {
      if (editing) await api.put(`/budget/transactions/${encodeURIComponent(tx.id)}`, payload);
      else await api.post(`/budget/artists/${artistId}/transactions`, payload);
      toast.success(editing ? "Transaction updated" : "Transaction added");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transaction-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit transaction" : "Add transaction"}</DialogTitle>
          <DialogDescription>Assign a fund and the album (or mark as an unassigned single). Leave amount blank if the total is unknown — it stays “Unreconciled”.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Fund</Label>
              <Select value={fund} onValueChange={setFund}>
                <SelectTrigger data-testid="tx-fund"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FUND_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Album</Label>
              <Select value={albumId} onValueChange={(v) => { setAlbumId(v); if (v !== NONE) setIsSingle(false); }}>
                <SelectTrigger data-testid="tx-album"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Not allocated</SelectItem>
                  {albums.map((al) => <SelectItem key={al.id} value={al.id}>{al.title || al.slug}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {albumId === NONE && (
            <label className="flex items-center gap-2 rounded-lg bg-[#F7EFDD]/70 px-3 py-2 text-sm">
              <Switch data-testid="tx-single" checked={isSingle} onCheckedChange={setIsSingle} />
              This is a single awaiting album assignment
            </label>
          )}

          <div className="grid gap-1.5">
            <Label>Vendor</Label>
            <Input data-testid="tx-vendor" value={vendor} onChange={(e) => setVendor(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label>Description</Label>
            <Input data-testid="tx-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Amount ($)</Label>
              <Input data-testid="tx-amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Unreconciled" />
            </div>
            <div className="grid gap-1.5">
              <Label>Date</Label>
              <Input data-testid="tx-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>AP group</Label>
              <Select value={apGroup} onValueChange={setApGroup}>
                <SelectTrigger data-testid="tx-apgroup"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  <SelectItem value="Paid">Paid (submitted)</SelectItem>
                  <SelectItem value="New Payment Requests">New Payment Requests</SelectItem>
                  <SelectItem value="Archives">Archives</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Payment status</Label>
              <Input data-testid="tx-status" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} placeholder="e.g. Full Payment Submitted" />
            </div>
          </div>
          <label className="flex items-center gap-2 rounded-lg bg-indigo-50/60 px-3 py-2 text-sm">
            <Switch data-testid="tx-bookmarked" checked={bookmarked} onCheckedChange={setBookmarked} />
            Bookmarked / planned cost (not paid) — counts toward committed, never toward paid
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch data-testid="tx-include" checked={include} onCheckedChange={setInclude} />
            Include in budget rollups
          </label>
          <div className="grid gap-1.5">
            <Label>Note</Label>
            <Textarea data-testid="tx-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button data-testid="tx-save" onClick={submit} disabled={saving} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} {editing ? "Save" : "Add transaction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
